import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription
} from '../ui/alert-dialog';
import { Plus, Pencil, Trash2, Search, Tag, QrCode, FileText } from 'lucide-react';
import { apiListProducts, apiCreateProduct, apiUpdateProduct, apiDeleteProduct, apiOpenProductLabel, apiLabelZpl, apiListPartners } from '../../erpApi';
import { toast } from 'sonner';

const KIND_LABEL = {
  finished: 'Prodotto finito',
  raw_material: 'Materia prima',
  semi_finished: 'Semilavorato',
  service: 'Servizio'
};
const KIND_COLOR = {
  finished: 'bg-blue-100 text-blue-700',
  raw_material: 'bg-amber-100 text-amber-700',
  semi_finished: 'bg-violet-100 text-violet-700',
  service: 'bg-slate-100 text-slate-700'
};

const emptyP = {
  sku: '', barcode: '', name: '', description: '',
  kind: 'finished', category: '', unit_of_measure: 'pz',
  purchase_price: 0, sale_price: 0, vat_rate: 22, min_stock: 0,
  active: true, notes: '', customer_id: null, lot_number: ''
};

const ProductForm = ({ initial, customers, onSubmit, onCancel }) => {
  const isEdit = !!initial;
  const [f, setF] = useState(initial ? { ...emptyP, ...initial } : emptyP);
  const submit = (e) => {
    e.preventDefault();
    if (!f.sku.trim() || !f.name.trim()) { toast.error('SKU e nome obbligatori'); return; }
    onSubmit({
      ...f,
      purchase_price: parseFloat(f.purchase_price) || 0,
      sale_price: parseFloat(f.sale_price) || 0,
      vat_rate: parseFloat(f.vat_rate) || 0,
      min_stock: parseFloat(f.min_stock) || 0,
      customer_id: f.customer_id || null,
    });
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="product-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>SKU *</Label><Input data-testid="product-sku" value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} className="font-mono uppercase" required /></div>
        <div className="space-y-1.5"><Label>Tipo *</Label>
          <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="finished">Prodotto finito</SelectItem>
              <SelectItem value="raw_material">Materia prima</SelectItem>
              <SelectItem value="semi_finished">Semilavorato</SelectItem>
              <SelectItem value="service">Servizio</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5"><Label>Nome *</Label><Input data-testid="product-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></div>
      <div className="space-y-1.5"><Label>Descrizione</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label>Categoria</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>U.M.</Label>
          <Select value={f.unit_of_measure} onValueChange={(v) => setF({ ...f, unit_of_measure: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pz">pz</SelectItem><SelectItem value="kg">kg</SelectItem>
              <SelectItem value="g">g</SelectItem><SelectItem value="lt">lt</SelectItem>
              <SelectItem value="m">m</SelectItem><SelectItem value="cm">cm</SelectItem>
              <SelectItem value="cf">cf</SelectItem><SelectItem value="cd">cd (cad.)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Barcode (EAN)</Label><Input value={f.barcode} onChange={(e) => setF({ ...f, barcode: e.target.value })} placeholder="es. 8001234567890" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Cliente proprietario {!isEdit && <span className="text-red-600">*</span>}</Label>
          <Select value={f.customer_id || ''} onValueChange={(v) => setF({ ...f, customer_id: v || null })}>
            <SelectTrigger data-testid="product-customer"><SelectValue placeholder={isEdit ? 'Nessuno (legacy)' : 'Seleziona cliente...'} /></SelectTrigger>
            <SelectContent>
              {customers.map(c => <SelectItem key={c.id} value={c.id}>[{c.code}] {c.business_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Lotto attivo</Label>
          <Input data-testid="product-lot" value={f.lot_number || ''} onChange={(e) => setF({ ...f, lot_number: e.target.value })} placeholder="es. L20260301" className="font-mono" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div className="space-y-1.5"><Label>Costo €</Label><Input type="number" step="0.001" value={f.purchase_price} onChange={(e) => setF({ ...f, purchase_price: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Vendita €</Label><Input type="number" step="0.01" value={f.sale_price} onChange={(e) => setF({ ...f, sale_price: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>IVA %</Label><Input type="number" step="0.01" value={f.vat_rate} onChange={(e) => setF({ ...f, vat_rate: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Scorta min.</Label><Input type="number" step="0.01" value={f.min_stock} onChange={(e) => setF({ ...f, min_stock: e.target.value })} /></div>
      </div>
      <div className="flex items-center justify-between p-2 rounded border border-slate-200"><Label className="mb-0">Attivo</Label><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        <Button data-testid="product-submit" type="submit" className="bg-blue-700 hover:bg-blue-800">{isEdit ? 'Salva' : 'Crea'}</Button>
      </DialogFooter>
    </form>
  );
};

const ProductsManager = () => {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [kindFilter, setKindFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const reload = async () => {
    const params = {};
    if (kindFilter !== 'all') params.kind = kindFilter;
    if (customerFilter !== 'all') params.customer_id = customerFilter;
    if (search) params.search = search;
    const [items, parts] = await Promise.all([apiListProducts(params), apiListPartners({ kind: 'customer' })]);
    setList(items); setCustomers(parts);
  };
  useEffect(() => { reload(); }, [kindFilter, customerFilter, search]); // eslint-disable-line
  const handleSubmit = async (data) => {
    try {
      if (editing) await apiUpdateProduct(editing.id, data); else await apiCreateProduct(data);
      toast.success('OK'); setDialogOpen(false); setEditing(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const handleDelete = async () => {
    try { await apiDeleteProduct(toDelete.id); toast.success('Eliminato'); setToDelete(null); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const downloadZpl = async (p) => {
    try {
      const zpl = await apiLabelZpl(p.id, 1);
      const blob = new Blob([zpl], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `label-${p.sku}.zpl`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error('Errore ZPL'); }
  };
  return (
    <div className="space-y-3" data-testid="products-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="h-9 w-[170px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              <SelectItem value="finished">Prodotti finiti</SelectItem>
              <SelectItem value="raw_material">Materie prime</SelectItem>
              <SelectItem value="semi_finished">Semilavorati</SelectItem>
              <SelectItem value="service">Servizi</SelectItem>
            </SelectContent>
          </Select>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger data-testid="product-customer-filter" className="h-9 w-[200px] text-sm"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.business_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="product-search" placeholder="Cerca nome, SKU, barcode..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 w-[260px] text-sm" />
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="product-new" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-blue-700 hover:bg-blue-800 gap-2 h-9"><Plus className="w-4 h-4" />Nuovo prodotto</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Modifica prodotto' : 'Nuovo prodotto'}</DialogTitle><DialogDescription>Anagrafica articoli (PF, MP, SL, servizi).</DialogDescription></DialogHeader>
            <ProductForm initial={editing} customers={customers} onSubmit={handleSubmit} onCancel={() => { setDialogOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300"><Tag className="w-10 h-10 text-slate-300 mx-auto mb-2" /><div className="text-sm">Nessun prodotto</div></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Tipo</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">SKU</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Nome</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Cliente</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Lotto</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">U.M.</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Costo €</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Vendita €</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">IVA</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Min</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map(p => (
                <tr key={p.id} className={`hover:bg-slate-50 ${!p.active ? 'opacity-60' : ''}`}>
                  <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${KIND_COLOR[p.kind]}`}>{KIND_LABEL[p.kind]}</span></td>
                  <td className="px-3 py-2 font-mono font-medium text-slate-900">{p.sku}</td>
                  <td className="px-3 py-2 text-slate-700">{p.name}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{p.customer_name || '—'}</td>
                  <td className="px-3 py-2 text-xs font-mono">{p.lot_number || '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{p.unit_of_measure}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.purchase_price?.toFixed(3) || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{p.sale_price?.toFixed(2) || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{p.vat_rate}%</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{p.min_stock || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Etichetta PDF" data-testid={`product-label-${p.sku}`} onClick={() => apiOpenProductLabel(p.id, 1).catch(() => toast.error('Errore etichetta'))}><QrCode className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => downloadZpl(p)} title="Scarica ZPL (Zebra)"><FileText className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(p); setDialogOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => setToDelete(p)}><Trash2 className="w-3 h-3" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Eliminare {toDelete?.name}?</AlertDialogTitle><AlertDialogDescription>I prodotti con movimenti di magazzino non possono essere eliminati.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsManager;
